import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IndianRupee, MessageCircle, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const currentMonthYear = () => { const d = new Date(); return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`; };
const MONTH_OPTIONS = (() => {
  const now = new Date();
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  });
})();

export default function PendingTab({ owner }) {
  const [month, setMonth] = useState(currentMonthYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (m = month) => {
    setLoading(true);
    try {
      const r = await api.get("/pending-payments", { params: { month_year: m } });
      setData(r.data);
    } catch { toast.error("Failed to load pending payments"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remind = (t) => {
    const msg = `Hi ${t.full_name},\n\nThis is a friendly reminder from ${owner?.property_name}. Your rent of ₹ ${Number(t.monthly_rent).toLocaleString("en-IN")} for ${data?.month_year} is still pending.\n\nPlease pay at your earliest convenience. Thank you!\n— ${owner?.property_name}`;
    const num = (t.whatsapp || "").replace(/[^0-9]/g, "");
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
    toast.success("WhatsApp reminder opened");
  };

  return (
    <Card className="p-6 border-slate-200" data-testid="pending-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-xl font-semibold flex items-center gap-2"><IndianRupee className="h-5 w-5 text-amber-500" /> Pending Payments</h2>
          <p className="text-sm text-slate-500 mt-0.5">Active tenants who haven't paid for the selected month.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={month} onValueChange={(v) => { setMonth(v); load(v); }}>
            <SelectTrigger className="w-44" data-testid="pending-month-select"><SelectValue placeholder="Select month" /></SelectTrigger>
            <SelectContent>
              {MONTH_OPTIONS.map((m) => (
                <SelectItem key={m} value={m} data-testid={`pending-month-option-${m.replace(" ", "-")}`}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => load()} disabled={loading} data-testid="pending-refresh-button">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Check
          </Button>
        </div>
      </div>

      {data && (
        <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm" data-testid="pending-summary">
          <span className="font-semibold text-amber-800">{data.pending_count} tenant(s)</span>
          <span className="text-amber-700"> pending for {data.month_year} — total </span>
          <span className="font-semibold text-amber-800">₹ {data.pending_amount.toLocaleString("en-IN")}</span>
        </div>
      )}

      {!data || data.pending.length === 0 ? (
        <div className="py-12 text-center text-slate-500">All active tenants have paid for {data?.month_year || month}.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <Th>Name</Th><Th>Room</Th><Th>WhatsApp</Th><Th>Rent due</Th><Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {data.pending.map((t) => (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pending-row-${t.id}`}>
                  <Td className="font-medium">{t.full_name}</Td>
                  <Td className="font-mono text-xs">{t.room_no}</Td>
                  <Td className="font-mono text-xs">{t.whatsapp}</Td>
                  <Td className="font-semibold text-amber-700">₹ {Number(t.monthly_rent).toLocaleString("en-IN")}</Td>
                  <Td className="text-right">
                    <Button size="sm" className="bg-[#25D366] hover:bg-[#1ebd59] text-white" onClick={() => remind(t)} data-testid={`pending-remind-btn-${t.id}`}>
                      <MessageCircle className="h-4 w-4 mr-1.5" /> Remind
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

const Th = ({ children, className = "" }) => <th className={`py-3 pr-4 font-semibold ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`py-3 pr-4 ${className}`}>{children}</td>;
